import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDropzone, FileRejection, ErrorCode } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import { v4 as generateId } from 'uuid';
import container from 'src/container';
import {
    OrganizationModuleType,
    canEmployeeReadOrganizationModule,
    isOrganizationModuleEnabled,
    isOrganizationModuleMember,
} from 'src/domain/organization';
import {
    InfoScreen as InfoScreenModel,
    InfoScreenSlide,
    InfoScreenSlideFit,
} from 'src/domain/infoScreen';
import {
    SASInfo,
    UserRole,
    getPrimaryRole,
    isOrganizationAdministrator,
} from 'src/domain/user';
import { CopyIcon, ScreenIcon } from 'src/components/Icons/icons';
import ConfimationModalProvider from 'src/components/Modals/ConfirmationModal/ConfirmationModalProvider';
import { ConfirmationButton } from 'src/components/Modals/ConfirmationModal/ConfirmationButton';
import { useNotification } from 'src/components/Notification/useNotfication';
import { useCurrentOrganization } from 'src/view/hooks/useCurrentOrganization';
import { useSignedInUser } from 'src/view/hooks/useSignedInUser';
import { ModulePageLayout } from '../components/ModulePageLayout';
import { ModuleUnavailableState } from '../components/ModuleUnavailableState';

const {
    cradle: { munduClient, storageService },
} = container;

const MAX_SLIDES = 30;
const MIN_DURATION = 3;
const MAX_DURATION = 120;

const normalizeFileName = (name: string) =>
    name
        .toLowerCase()
        .replace(/[^a-z0-9.]+/g, '-')
        .replace(/^-+|-+$/g, '');

const imageExtensionPattern =
    /\.(png|jpe?g|webp|gif|bmp|svg|heic|heif|tiff?)$/i;

const isImageFile = (file: File) =>
    file.type.startsWith('image/') || imageExtensionPattern.test(file.name);

const serializeDraft = (duration: number, slides: InfoScreenSlide[]) =>
    JSON.stringify({
        duration,
        slides: slides.map(slide => ({
            id: slide.id,
            status: slide.status,
            fit: slide.fit,
        })),
    });

export const InfoScreen = () => {
    const { t } = useTranslation();
    const notify = useNotification();
    const user = useSignedInUser();
    const role = getPrimaryRole(user);
    const { organization, isLoading: organizationLoading } =
        useCurrentOrganization();
    const infoScreenEnabled = isOrganizationModuleEnabled(
        organization,
        OrganizationModuleType.infoScreen,
    );
    const isInfoScreenMember = isOrganizationModuleMember(
        organization,
        OrganizationModuleType.infoScreen,
        user?.id,
    );
    const canManageInfoScreen = !user?.supportMode && isInfoScreenMember;
    const hasInfoScreenAccess =
        infoScreenEnabled &&
        (isOrganizationAdministrator(user) ||
            role !== UserRole.creator ||
            canEmployeeReadOrganizationModule(
                organization,
                OrganizationModuleType.infoScreen,
                user?.id,
            ));
    const [infoScreen, setInfoScreen] = useState<InfoScreenModel>();
    const [slides, setSlides] = useState<InfoScreenSlide[]>([]);
    const [duration, setDuration] = useState(10);
    const [sas, setSas] = useState<SASInfo>();
    const [selectedSlideId, setSelectedSlideId] = useState<string>();
    const [draggedSlideId, setDraggedSlideId] = useState<string>();
    const [dropTargetId, setDropTargetId] = useState<string>();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);

    const activeSlides = useMemo(
        () => slides.filter(slide => slide.status === 'active'),
        [slides],
    );
    const selectedSlide = useMemo(
        () => slides.find(slide => slide.id === selectedSlideId) ?? slides[0],
        [selectedSlideId, slides],
    );
    const selectedIndex = selectedSlide
        ? slides.findIndex(slide => slide.id === selectedSlide.id)
        : -1;
    const publicUrl = infoScreen
        ? `${window.location.origin}${infoScreen.publicPath}`
        : '';

    const hasUnsavedChanges = useMemo(() => {
        if (!infoScreen) return false;
        return (
            serializeDraft(duration, slides) !==
            serializeDraft(infoScreen.slideDurationSeconds, infoScreen.slides)
        );
    }, [duration, infoScreen, slides]);

    const loadInfoScreen = useCallback(async () => {
        setLoading(true);
        const [screen, uploadSas] = await Promise.all([
            munduClient.getInfoScreen(),
            canManageInfoScreen
                ? munduClient.getInfoScreenSas()
                : Promise.resolve(undefined),
        ]);
        setInfoScreen(screen);
        setDuration(screen.slideDurationSeconds);
        setSlides(screen.slides);
        setSelectedSlideId(screen.slides[0]?.id);
        setSas(uploadSas);
        setLoading(false);
    }, [canManageInfoScreen]);

    useEffect(() => {
        if (!hasInfoScreenAccess) return;

        loadInfoScreen().catch(error => {
            notify(error.message, true);
            setLoading(false);
        });
    }, [hasInfoScreenAccess, loadInfoScreen, notify]);

    const saveChanges = useCallback(async () => {
        setSaving(true);
        try {
            const saved = await munduClient.updateInfoScreen({
                slideDurationSeconds: duration,
                slides: slides.map((slide, index) => ({
                    ...slide,
                    order: index,
                })),
            });
            setInfoScreen(saved);
            setSlides(saved.slides);
            notify(t('modules.infoScreen.saved'));
        } catch (error) {
            notify((error as Error).message, true);
        } finally {
            setSaving(false);
        }
    }, [duration, notify, slides, t]);

    const handleAcceptedFiles = useCallback(
        async (files: File[]) => {
            if (!sas) return;

            const imageFiles = files.filter(isImageFile);
            const rejectedCount = files.length - imageFiles.length;
            if (rejectedCount > 0) {
                notify(t('modules.infoScreen.onlyImages'), true);
            }

            const remaining = MAX_SLIDES - slides.length;
            const acceptedFiles = imageFiles.slice(0, remaining);

            if (imageFiles.length > remaining) {
                notify(
                    t('modules.infoScreen.maxSlides', { max: MAX_SLIDES }),
                    true,
                );
            }

            if (!acceptedFiles.length) {
                return;
            }

            setUploading(true);
            try {
                const uploadedSlides = await Promise.all(
                    acceptedFiles.map(async (file, offset) => {
                        const id = generateId();
                        const blobName = `${id}-${normalizeFileName(
                            file.name,
                        )}`;
                        await storageService.uploadImage(file, blobName, sas);

                        return {
                            id,
                            blobName,
                            originalName: file.name,
                            mimeType: file.type,
                            size: file.size,
                            status: 'active' as const,
                            fit: 'cover' as const,
                            order: slides.length + offset,
                            imageUrl: storageService.getImageUrl(blobName, sas),
                        };
                    }),
                );

                setSlides(current => [...current, ...uploadedSlides]);
                setSelectedSlideId(uploadedSlides[0]?.id);
            } catch (error) {
                notify((error as Error).message, true);
            } finally {
                setUploading(false);
            }
        },
        [notify, sas, slides.length, t],
    );

    const onDropRejected = useCallback(
        (fileRejections: FileRejection[]) => {
            const hasInvalidType = fileRejections.some(rejection =>
                rejection.errors.some(
                    error => error.code === ErrorCode.FileInvalidType,
                ),
            );
            notify(
                hasInvalidType
                    ? t('modules.infoScreen.onlyImages')
                    : t('modules.infoScreen.uploadFailed'),
                true,
            );
        },
        [notify, t],
    );

    const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
        onDropAccepted: handleAcceptedFiles,
        onDropRejected,
        multiple: true,
        noClick: true,
        disabled: !canManageInfoScreen,
    });

    const reorderSlides = (targetSlideId: string) => {
        if (!draggedSlideId || draggedSlideId === targetSlideId) return;

        setSlides(current => {
            const from = current.findIndex(
                slide => slide.id === draggedSlideId,
            );
            const to = current.findIndex(slide => slide.id === targetSlideId);
            if (from < 0 || to < 0) return current;

            const next = current.slice();
            const [moved] = next.splice(from, 1);
            next.splice(to, 0, moved);
            return next.map((slide, index) => ({ ...slide, order: index }));
        });
    };

    const moveSlide = (slideId: string, direction: -1 | 1) => {
        setSlides(current => {
            const from = current.findIndex(slide => slide.id === slideId);
            const to = from + direction;
            if (from < 0 || to < 0 || to >= current.length) return current;

            const next = current.slice();
            const [moved] = next.splice(from, 1);
            next.splice(to, 0, moved);
            return next.map((slide, index) => ({ ...slide, order: index }));
        });
    };

    const updateSlide = (
        slideId: string,
        patch: Partial<Pick<InfoScreenSlide, 'status' | 'fit'>>,
    ) => {
        setSlides(current =>
            current.map(slide =>
                slide.id === slideId ? { ...slide, ...patch } : slide,
            ),
        );
    };

    const deleteSlide = (slideId: string) => {
        const index = slides.findIndex(item => item.id === slideId);
        if (index < 0) return;

        const nextSelected = slides[index + 1] ?? slides[index - 1];
        setSlides(current =>
            current
                .filter(item => item.id !== slideId)
                .map((item, order) => ({ ...item, order })),
        );
        setSelectedSlideId(current =>
            current === slideId ? nextSelected?.id : current,
        );
    };

    const regenerateLink = async () => {
        try {
            const regenerated = await munduClient.regenerateInfoScreenLink();
            // keep local slide edits; only the link changes on the server
            setInfoScreen(current =>
                current
                    ? {
                          ...current,
                          token: regenerated.token,
                          publicPath: regenerated.publicPath,
                      }
                    : regenerated,
            );
            notify(t('modules.infoScreen.linkRegenerated'));
        } catch (error) {
            notify((error as Error).message, true);
        }
    };

    const copyLink = async () => {
        if (!publicUrl) return;
        await navigator.clipboard.writeText(publicUrl);
        notify(t('modules.infoScreen.linkCopied'));
    };

    const clampDuration = () => {
        setDuration(current => {
            if (!Number.isFinite(current)) return MIN_DURATION;
            return Math.min(MAX_DURATION, Math.max(MIN_DURATION, current));
        });
    };

    const stepDuration = (delta: number) => {
        setDuration(current => {
            const base = Number.isFinite(current) ? current : 10;
            return Math.min(MAX_DURATION, Math.max(MIN_DURATION, base + delta));
        });
    };

    if (organizationLoading) {
        return (
            <div className="h-full flex items-center justify-center text-woodyBrown">
                {t('modules.infoScreen.loading')}
            </div>
        );
    }

    if (organization && !infoScreenEnabled) {
        return (
            <ModuleUnavailableState
                icon={ScreenIcon}
                moduleName={t('modules.infoScreen.navLabel')}
                reason="notEnabled"
            />
        );
    }

    if (organization && !hasInfoScreenAccess) {
        return (
            <ModuleUnavailableState
                icon={ScreenIcon}
                moduleName={t('modules.infoScreen.navLabel')}
                reason="accessRequired"
            />
        );
    }

    if (loading) {
        return (
            <div className="h-full flex items-center justify-center text-woodyBrown">
                {t('modules.infoScreen.loading')}
            </div>
        );
    }

    if (!canManageInfoScreen) {
        return (
            <ModulePageLayout
                basePath="/app/info-screen"
                canManageSettings={
                    !user?.supportMode && isOrganizationAdministrator(user)
                }
            >
                <div className="h-full overflow-y-auto px-1 pb-8 text-woodyBrown">
                    <p className="mb-5 rounded-2xl bg-white bg-opacity-50 px-4 py-3 text-sm">
                        {t('modules.infoScreen.readOnlyNotice')}
                    </p>

                    <div className="mb-5">
                        <h2 className="font-logo text-2xl">
                            {t('modules.infoScreen.slidesTitle')}
                        </h2>
                        <p className="mt-1 text-sm opacity-60">
                            {t('modules.infoScreen.readOnlySlidesShown', {
                                count: activeSlides.length,
                            })}
                        </p>
                    </div>

                    <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_340px]">
                        <section className="rounded-2xl bg-white bg-opacity-50 p-5 sm:p-6">
                            {activeSlides.length ? (
                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 2xl:grid-cols-3">
                                    {activeSlides.map((slide, index) => (
                                        <figure
                                            key={slide.id}
                                            className="overflow-hidden rounded-xl bg-white shadow-sm"
                                        >
                                            {slide.imageUrl && (
                                                <img
                                                    src={slide.imageUrl}
                                                    alt={slide.originalName}
                                                    className="aspect-video w-full object-cover"
                                                />
                                            )}
                                            <figcaption className="truncate px-3 py-2 text-sm">
                                                {index + 1}.{' '}
                                                {slide.originalName}
                                            </figcaption>
                                        </figure>
                                    ))}
                                </div>
                            ) : (
                                <p className="py-12 text-center text-sm opacity-60">
                                    {t('modules.infoScreen.previewEmpty')}
                                </p>
                            )}
                        </section>

                        <aside>
                            <h3 className="mb-3 font-logo text-lg">
                                {t('modules.infoScreen.previewTitle')}
                            </h3>
                            <TvPreview
                                slides={activeSlides}
                                duration={duration}
                            />

                            <div className="mt-5 rounded-2xl bg-white bg-opacity-50 p-5">
                                <h3 className="mb-1 font-logo text-lg">
                                    {t('modules.infoScreen.tvLinkTitle')}
                                </h3>
                                <p className="mb-3 text-sm opacity-75">
                                    {t('modules.infoScreen.tvLinkDescription')}
                                </p>
                                <button
                                    type="button"
                                    className="group flex w-full items-start gap-2 rounded-lg bg-white px-3 py-2 text-left transition-colors duration-100 hover:bg-opacity-70"
                                    title={t('modules.infoScreen.copyLink')}
                                    aria-label={t(
                                        'modules.infoScreen.copyLink',
                                    )}
                                    onClick={copyLink}
                                >
                                    <span className="flex-1 break-all text-sm opacity-70 group-hover:opacity-100">
                                        {publicUrl}
                                    </span>
                                    <CopyIcon className="mt-0.5 h-4 w-4 shrink-0 opacity-60 group-hover:opacity-100" />
                                </button>
                            </div>
                        </aside>
                    </div>
                </div>
            </ModulePageLayout>
        );
    }

    return (
        <ModulePageLayout
            basePath="/app/info-screen"
            canManageSettings={
                !user?.supportMode && isOrganizationAdministrator(user)
            }
        >
            <div className="h-full overflow-y-auto px-1 pb-8 text-woodyBrown">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                    <div>
                        <h2 className="text-2xl font-logo">
                            {t('modules.infoScreen.slidesTitle')}
                        </h2>
                        <p className="text-sm opacity-60">
                            {t('modules.infoScreen.slidesShown', {
                                active: activeSlides.length,
                                total: slides.length,
                            })}
                        </p>
                    </div>
                    <div className="flex flex-wrap items-center justify-end gap-3">
                        {hasUnsavedChanges && (
                            <p className="text-sm opacity-60">
                                {t('modules.infoScreen.unsavedChanges')}
                            </p>
                        )}
                        <SaveButton
                            title={
                                saving
                                    ? t('modules.infoScreen.saving')
                                    : t('modules.infoScreen.saveChanges')
                            }
                            disabled={!hasUnsavedChanges || saving || uploading}
                            onClick={saveChanges}
                        />
                    </div>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_340px] gap-6">
                    <main {...getRootProps()} className="relative min-w-0">
                        <input {...getInputProps()} />
                        {isDragActive && (
                            <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg border-2 border-dashed border-woodyBrown bg-lightPeach bg-opacity-90">
                                <p className="text-lg font-medium">
                                    {t('modules.infoScreen.dropToAdd')}
                                </p>
                            </div>
                        )}

                        <div className="flex items-center gap-3 mb-4">
                            <label
                                className="text-sm font-medium"
                                htmlFor="duration"
                            >
                                {t('modules.infoScreen.secondsPerSlide')}
                            </label>
                            <div className="flex items-center gap-1.5">
                                <button
                                    type="button"
                                    aria-label="−5"
                                    disabled={duration <= MIN_DURATION}
                                    className="h-9 w-9 rounded-full border-1.6px border-woodyBrown text-lg leading-none transition-colors duration-100 hover:bg-woodyBrown hover:text-white disabled:border-mongooseGray disabled:text-mongooseGray disabled:pointer-events-none"
                                    onClick={() => stepDuration(-5)}
                                >
                                    −
                                </button>
                                <input
                                    id="duration"
                                    className="w-16 rounded-full border-1.6px border-woodyBrown border-opacity-20 bg-white px-2 py-1.5 text-center"
                                    type="number"
                                    min={MIN_DURATION}
                                    max={MAX_DURATION}
                                    value={
                                        Number.isFinite(duration)
                                            ? duration
                                            : ''
                                    }
                                    onChange={event =>
                                        setDuration(
                                            event.currentTarget.valueAsNumber,
                                        )
                                    }
                                    onBlur={clampDuration}
                                />
                                <button
                                    type="button"
                                    aria-label="+5"
                                    disabled={duration >= MAX_DURATION}
                                    className="h-9 w-9 rounded-full border-1.6px border-woodyBrown text-lg leading-none transition-colors duration-100 hover:bg-woodyBrown hover:text-white disabled:border-mongooseGray disabled:text-mongooseGray disabled:pointer-events-none"
                                    onClick={() => stepDuration(5)}
                                >
                                    +
                                </button>
                            </div>
                        </div>

                        <div className="flex gap-3 overflow-x-auto pb-1">
                            {slides.map((slide, index) => (
                                <div
                                    key={slide.id}
                                    draggable
                                    onDragStart={() =>
                                        setDraggedSlideId(slide.id)
                                    }
                                    onDragEnd={() => {
                                        setDraggedSlideId(undefined);
                                        setDropTargetId(undefined);
                                    }}
                                    onDragOver={event => {
                                        event.preventDefault();
                                        setDropTargetId(slide.id);
                                    }}
                                    onDragLeave={() =>
                                        setDropTargetId(current =>
                                            current === slide.id
                                                ? undefined
                                                : current,
                                        )
                                    }
                                    onDrop={() => {
                                        reorderSlides(slide.id);
                                        setDraggedSlideId(undefined);
                                        setDropTargetId(undefined);
                                    }}
                                    className={cx(
                                        'relative w-44 h-28 shrink-0 overflow-hidden rounded-lg border bg-white shadow-sm transition-all cursor-grab active:cursor-grabbing',
                                        selectedSlide?.id === slide.id
                                            ? 'border-woodyBrown ring-1 ring-woodyBrown'
                                            : 'border-woodyBrown border-opacity-10',
                                        draggedSlideId === slide.id &&
                                            'opacity-40',
                                        dropTargetId === slide.id &&
                                            draggedSlideId !== slide.id &&
                                            'ring-2 ring-woodyBrown',
                                    )}
                                >
                                    <button
                                        type="button"
                                        aria-label={slide.originalName}
                                        className="absolute inset-0 h-full w-full"
                                        onClick={() =>
                                            setSelectedSlideId(slide.id)
                                        }
                                    >
                                        {slide.imageUrl && (
                                            <img
                                                src={slide.imageUrl}
                                                alt={slide.originalName}
                                                className={cx(
                                                    'h-full w-full object-cover pointer-events-none',
                                                    slide.status !== 'active' &&
                                                        'opacity-40',
                                                )}
                                            />
                                        )}
                                    </button>
                                    <span className="pointer-events-none absolute left-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-woodyBrown text-[11px] font-semibold text-white">
                                        {index + 1}
                                    </span>
                                    {slide.status !== 'active' && (
                                        <span className="pointer-events-none absolute right-2 top-2 rounded-full bg-white px-2 py-0.5 text-[11px] font-semibold uppercase text-woodyBrown">
                                            {t(
                                                'modules.infoScreen.hiddenBadge',
                                            )}
                                        </span>
                                    )}
                                    {selectedSlide?.id === slide.id &&
                                        slides.length > 1 && (
                                            <div className="absolute bottom-1.5 right-1.5 flex gap-1">
                                                <button
                                                    type="button"
                                                    disabled={index <= 0}
                                                    title={t(
                                                        'modules.infoScreen.moveEarlier',
                                                    )}
                                                    aria-label={t(
                                                        'modules.infoScreen.moveEarlier',
                                                    )}
                                                    className="flex h-6 w-6 items-center justify-center rounded-full bg-white text-woodyBrown shadow transition-colors duration-100 hover:bg-woodyBrown hover:text-white disabled:opacity-40 disabled:pointer-events-none"
                                                    onClick={() =>
                                                        moveSlide(slide.id, -1)
                                                    }
                                                >
                                                    ‹
                                                </button>
                                                <button
                                                    type="button"
                                                    disabled={
                                                        index >=
                                                        slides.length - 1
                                                    }
                                                    title={t(
                                                        'modules.infoScreen.moveLater',
                                                    )}
                                                    aria-label={t(
                                                        'modules.infoScreen.moveLater',
                                                    )}
                                                    className="flex h-6 w-6 items-center justify-center rounded-full bg-white text-woodyBrown shadow transition-colors duration-100 hover:bg-woodyBrown hover:text-white disabled:opacity-40 disabled:pointer-events-none"
                                                    onClick={() =>
                                                        moveSlide(slide.id, 1)
                                                    }
                                                >
                                                    ›
                                                </button>
                                            </div>
                                        )}
                                </div>
                            ))}
                            <button
                                type="button"
                                onClick={open}
                                disabled={
                                    uploading || slides.length >= MAX_SLIDES
                                }
                                className="w-44 h-28 shrink-0 rounded-lg border-2 border-dashed border-woodyBrown border-opacity-25 bg-white bg-opacity-40 font-medium disabled:text-mongooseGray"
                            >
                                {uploading
                                    ? t('modules.infoScreen.uploading')
                                    : `+ ${t('modules.infoScreen.addSlide')}`}
                            </button>
                        </div>
                        {slides.length > 1 && (
                            <p className="text-xs opacity-60 mt-1 mb-4">
                                {t('modules.infoScreen.reorderHint')}
                            </p>
                        )}

                        <section
                            className={cx(
                                'rounded-lg border border-woodyBrown border-opacity-10 bg-white p-5 shadow-sm',
                                slides.length > 1 ? 'mt-1' : 'mt-4',
                            )}
                        >
                            {selectedSlide ? (
                                <SlideEditor
                                    slide={selectedSlide}
                                    index={selectedIndex}
                                    total={slides.length}
                                    onNavigate={direction => {
                                        const next =
                                            slides[selectedIndex + direction];
                                        if (next) setSelectedSlideId(next.id);
                                    }}
                                    onDelete={() =>
                                        deleteSlide(selectedSlide.id)
                                    }
                                    onUpdate={patch =>
                                        updateSlide(selectedSlide.id, patch)
                                    }
                                />
                            ) : (
                                <div className="aspect-video rounded-lg border-2 border-dashed border-woodyBrown border-opacity-20 flex flex-col items-center justify-center text-center">
                                    <button
                                        type="button"
                                        className="primary-btn"
                                        onClick={open}
                                    >
                                        {t('modules.infoScreen.uploadImage')}
                                    </button>
                                    <p className="mt-3 text-sm opacity-60">
                                        {t('modules.infoScreen.uploadHint')}
                                    </p>
                                </div>
                            )}
                        </section>
                    </main>

                    <aside>
                        <h3 className="font-logo text-lg mb-3">
                            {t('modules.infoScreen.previewTitle')}
                        </h3>
                        <TvPreview slides={activeSlides} duration={duration} />

                        <div className="mt-5 rounded-lg bg-white bg-opacity-50 p-5">
                            <h3 className="font-logo text-lg mb-1">
                                {t('modules.infoScreen.tvLinkTitle')}
                            </h3>
                            <p className="text-sm opacity-75 mb-3">
                                {t('modules.infoScreen.tvLinkDescription')}
                            </p>
                            <button
                                type="button"
                                className="group flex w-full items-start gap-2 rounded-lg border border-woodyBrown border-opacity-15 bg-white px-3 py-2 text-left transition-colors duration-100 hover:border-opacity-40 mb-3"
                                title={t('modules.infoScreen.copyLink')}
                                aria-label={t('modules.infoScreen.copyLink')}
                                onClick={copyLink}
                            >
                                <span className="flex-1 break-all text-sm opacity-70 group-hover:opacity-100">
                                    {publicUrl}
                                </span>
                                <CopyIcon className="mt-0.5 h-4 w-4 shrink-0 opacity-60 group-hover:opacity-100" />
                            </button>
                            <div className="flex flex-wrap gap-2">
                                <ConfimationModalProvider
                                    title={t(
                                        'modules.infoScreen.regenerateConfirmTitle',
                                    )}
                                    message={t(
                                        'modules.infoScreen.regenerateConfirmMessage',
                                    )}
                                >
                                    <ConfirmationButton
                                        type="button"
                                        className="secondary-btn-small"
                                        onClick={regenerateLink}
                                    >
                                        {t('modules.infoScreen.regenerate')}
                                    </ConfirmationButton>
                                </ConfimationModalProvider>
                            </div>
                        </div>

                        <div className="mt-5 rounded-lg bg-white bg-opacity-50 p-5">
                            <h3 className="font-logo text-lg mb-2">
                                {t('modules.infoScreen.setupTitle')}
                            </h3>
                            <ol className="list-decimal pl-5 text-sm space-y-2 opacity-75">
                                <li>{t('modules.infoScreen.setupStep1')}</li>
                                <li>{t('modules.infoScreen.setupStep2')}</li>
                                <li>{t('modules.infoScreen.setupStep3')}</li>
                                <li>{t('modules.infoScreen.setupStep4')}</li>
                            </ol>
                        </div>
                    </aside>
                </div>
            </div>
        </ModulePageLayout>
    );
};

const SaveButton = ({
    title,
    disabled,
    onClick,
}: {
    title: string;
    disabled?: boolean;
    onClick: () => void;
}) => (
    <button
        type="button"
        disabled={disabled}
        onClick={onClick}
        className={cx(
            'base-btn btn-small lg:btn-large border-1.4px',
            disabled
                ? 'border-mongooseGray text-mongooseGray'
                : 'border-woodyBrown bg-woodyBrown text-lightPeach hover:bg-starkWhite hover:text-woodyBrown',
        )}
    >
        {title}
    </button>
);

const TvPreview = ({
    slides,
    duration,
}: {
    slides: InfoScreenSlide[];
    duration: number;
}) => {
    const { t } = useTranslation();
    const [index, setIndex] = useState(0);

    useEffect(() => {
        setIndex(0);
    }, [slides.length]);

    useEffect(() => {
        if (slides.length <= 1) return;

        const durationMs =
            Math.max(
                Number.isFinite(duration) ? duration : MIN_DURATION,
                MIN_DURATION,
            ) * 1000;
        const intervalId = window.setInterval(
            () => setIndex(current => current + 1),
            durationMs,
        );
        return () => window.clearInterval(intervalId);
    }, [duration, slides.length]);

    const slide = slides[index % slides.length];

    return (
        <div className="relative aspect-video overflow-hidden rounded-lg bg-woodyBrown text-white shadow-sm">
            {slide?.imageUrl ? (
                <>
                    <img
                        key={slide.id}
                        src={slide.imageUrl}
                        alt={slide.originalName}
                        className={cx(
                            'h-full w-full',
                            slide.fit === 'contain'
                                ? 'object-contain'
                                : 'object-cover',
                        )}
                    />
                    {slides.length > 1 && (
                        <span className="absolute bottom-2 right-2 rounded-full bg-woodyBrown bg-opacity-80 px-2 py-0.5 text-[11px]">
                            {(index % slides.length) + 1} / {slides.length}
                        </span>
                    )}
                </>
            ) : (
                <div className="flex h-full items-center justify-center px-6 text-center text-sm">
                    {t('modules.infoScreen.previewEmpty')}
                </div>
            )}
        </div>
    );
};

const SlideEditor = ({
    slide,
    index,
    total,
    onNavigate,
    onDelete,
    onUpdate,
}: {
    slide: InfoScreenSlide;
    index: number;
    total: number;
    onNavigate: (direction: -1 | 1) => void;
    onDelete: () => void;
    onUpdate: (patch: Partial<Pick<InfoScreenSlide, 'status' | 'fit'>>) => void;
}) => {
    const { t } = useTranslation();
    const isActive = slide.status === 'active';
    const fitOptions: { value: InfoScreenSlideFit; label: string }[] = [
        { value: 'cover', label: t('modules.infoScreen.fitCover') },
        { value: 'contain', label: t('modules.infoScreen.fitContain') },
    ];

    return (
        <div>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div className="min-w-0">
                    <h3 className="font-logo text-xl truncate">
                        {slide.originalName}
                    </h3>
                    <p className="text-sm opacity-60">
                        {t('modules.infoScreen.slideCounter', {
                            current: index + 1,
                            total,
                        })}
                        {' · '}
                        {t('modules.infoScreen.imageHint')}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        className="secondary-btn-small disabled:opacity-40 disabled:pointer-events-none"
                        disabled={index <= 0}
                        aria-label={t('modules.infoScreen.prevSlide')}
                        title={t('modules.infoScreen.prevSlide')}
                        onClick={() => onNavigate(-1)}
                    >
                        ←
                    </button>
                    <button
                        type="button"
                        className="secondary-btn-small disabled:opacity-40 disabled:pointer-events-none"
                        disabled={index >= total - 1}
                        aria-label={t('modules.infoScreen.nextSlide')}
                        title={t('modules.infoScreen.nextSlide')}
                        onClick={() => onNavigate(1)}
                    >
                        →
                    </button>
                    <ConfimationModalProvider
                        title={t('modules.infoScreen.deleteConfirmTitle')}
                        message={t('modules.infoScreen.deleteConfirmMessage', {
                            name: slide.originalName,
                        })}
                    >
                        <ConfirmationButton
                            type="button"
                            className="secondary-btn-small"
                            onClick={onDelete}
                        >
                            {t('modules.infoScreen.deleteSlide')}
                        </ConfirmationButton>
                    </ConfimationModalProvider>
                </div>
            </div>

            <div className="aspect-video overflow-hidden rounded-lg bg-woodyBrown bg-opacity-10">
                {slide.imageUrl && (
                    <img
                        src={slide.imageUrl}
                        alt={slide.originalName}
                        className={cx(
                            'h-full w-full',
                            slide.fit === 'contain'
                                ? 'object-contain'
                                : 'object-cover',
                        )}
                    />
                )}
            </div>

            <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                <button
                    type="button"
                    role="switch"
                    aria-checked={isActive}
                    className="flex items-center gap-2 outline-none focus:outline-none"
                    onClick={() =>
                        onUpdate({ status: isActive ? 'draft' : 'active' })
                    }
                >
                    <span
                        className={cx(
                            'relative h-6 w-11 shrink-0 rounded-full transition-colors duration-150',
                            isActive
                                ? 'bg-palmleafGreen'
                                : 'bg-woodyBrown bg-opacity-20',
                        )}
                    >
                        <span
                            className={cx(
                                'absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all duration-150',
                                isActive ? 'left-[22px]' : 'left-0.5',
                            )}
                        />
                    </span>
                    <span className="text-sm font-medium">
                        {isActive
                            ? t('modules.infoScreen.shownOnTv')
                            : t('modules.infoScreen.hiddenFromTv')}
                    </span>
                </button>

                <div className="flex items-center gap-2">
                    <span className="text-sm opacity-60">
                        {t('modules.infoScreen.fitLabel')}
                    </span>
                    <div className="flex items-center rounded-full bg-lightPeach border border-woodyBrown border-opacity-20 p-1">
                        {fitOptions.map(option => (
                            <button
                                key={option.value}
                                type="button"
                                className={cx(
                                    'rounded-full px-3 py-1.5 text-sm transition-colors duration-100',
                                    slide.fit === option.value
                                        ? 'bg-woodyBrown text-white'
                                        : 'text-woodyBrown',
                                )}
                                onClick={() => onUpdate({ fit: option.value })}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};
